import { useMemo, useState } from "react";
import { feedEvents, type FeedEvent } from "../../../mocks/trendData";

const statusStyle: Record<FeedEvent["status"], string> = {
  发酵中: "bg-primary-100 text-primary-700",
  已回应: "bg-secondary-100 text-secondary-800",
  已辟谣: "bg-accent-100 text-accent-800",
  新进展: "bg-accent-500 text-background-50",
};

const categories = ["全部", ...Array.from(new Set(feedEvents.map((e) => e.category)))];

export default function EventFeed() {
  const [active, setActive] = useState("全部");
  const [query, setQuery] = useState("");

  const list = useMemo(() => {
    return feedEvents.filter((e) => {
      const matchCat = active === "全部" || e.category === active;
      const q = query.trim();
      const matchQ = !q || e.title.includes(q) || e.summary.includes(q) || e.tags.some((t) => t.includes(q));
      return matchCat && matchQ;
    });
  }, [active, query]);

  return (
    <section id="feed" className="bg-background-100 py-10 md:py-14">
      <div className="container-x">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="section-kicker">最新动态追踪</span>
            <h2 className="mt-3 font-heading text-xl font-black tracking-tight text-foreground-950 md:text-3xl">
              紧跟最新黑料、新事件与新进展
            </h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              新后续、新动态随时补充。点开任意事件即可查看其最新走向、所处阶段与各方回应。
            </p>
          </div>
          <div className="relative w-full md:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" aria-hidden="true"></i>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索事件 / 关键词"
              aria-label="搜索事件"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2.5 pl-9 pr-9 text-sm text-foreground-800 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                aria-label="清空搜索"
                className="absolute right-2 top-1/2 -translate-y-1/2 flex h-6 w-6 items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700"
              >
                <i className="ri-close-line" aria-hidden="true"></i>
              </button>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => setActive(cat)}
              className={
                active === cat
                  ? "whitespace-nowrap rounded-full bg-primary-500 px-4 py-1.5 text-sm font-semibold text-background-50"
                  : "whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-4 py-1.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              }
            >
              {cat}
            </button>
          ))}
        </div>

        <div className="mt-6 space-y-3">
          {list.map((ev) => (
            <article
              key={ev.id}
              className="group grid grid-cols-1 items-start gap-3 rounded-lg border border-background-200 bg-background-50 p-4 transition hover:border-primary-200 hover:bg-background-100/60 md:grid-cols-12 md:p-5"
            >
              <div className="flex flex-wrap items-center gap-2 md:col-span-2">
                <span className="whitespace-nowrap rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-semibold text-secondary-800">
                  {ev.category}
                </span>
                <span className={`whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold ${statusStyle[ev.status]}`}>
                  {ev.status}
                </span>
              </div>
              <div className="md:col-span-8">
                <h3 className="text-sm font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
                  {ev.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{ev.summary}</p>
                <div className="mt-2.5 flex flex-wrap items-center gap-2">
                  {ev.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-background-200 px-2 py-0.5 text-[11px] text-foreground-600">
                      # {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 text-xs text-foreground-500 md:col-span-2 md:flex-col md:items-end md:justify-between">
                <div className="flex items-center gap-3 md:flex-col md:items-end md:gap-1.5">
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-fire-line text-primary-500" aria-hidden="true"></i>
                    <b className="text-foreground-800">{ev.heat}</b>
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <i className="ri-chat-3-line text-accent-600" aria-hidden="true"></i>
                    {ev.replies}
                  </span>
                </div>
                <span className="inline-flex items-center gap-1 whitespace-nowrap md:flex-col md:items-end">
                  <i className="ri-time-line" aria-hidden="true"></i>
                  {ev.time}
                </span>
              </div>
            </article>
          ))}
          {list.length === 0 && (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center text-sm text-foreground-500">
              没有找到匹配"{query}"的相关事件，换个关键词试试。
            </div>
          )}
        </div>
      </div>
    </section>
  );
}